import { useCallback, useContext, useState } from 'react';

import { useLazyGetBranchWorkTimesOnDateQuery } from '@app/api/Branches';
import { useLazyGetEmployeesRecordsQuery } from '@app/api/Records/api';
import { useLazyGetBranchEmployeesQuery } from '@app/api/Users';
import { WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import {
  useCalendarSettingsContext,
  useTeamCalendarContext,
} from '@app/providers/CalendarProvider/useCalendar';
import { handleCatchError } from '@app/services';
import type { TEmployee, NEmployees } from '@app/types';

import { adaptModelEmployeeRecords } from '../utils/adaptModelEmployeeRecords';

export const useCalendarAPI = () => {
  const [isGetBranchEmployeesLoading, setIsGetBranchEmployeesLoading] =
    useState(false);
  const { userId, database, activeBranchId } = useUserPrint();
  const { setIsLoading, setBranchTimezone, setTimeFormat } =
    useCalendarSettingsContext();
  const {
    setEmployeeDetails,
    setBranchWorkingHoursForDate,
    setLastStoredEmployeeDetails,
  } = useTeamCalendarContext();
  const { showNotification } = useContext(NotificationContext);

  const [getBranchWorkTimesOnDate] = useLazyGetBranchWorkTimesOnDateQuery();
  const [getBranchEmployees] = useLazyGetBranchEmployeesQuery();
  const [getEmployeesRecords] = useLazyGetEmployeesRecordsQuery();

  const getEmployeesData = useCallback(
    async ({
      date,
      noLoadingIndicator,
      onSuccess,
    }: {
      date: string;
      noLoadingIndicator?: boolean;
      onSuccess?: (employeeDetails: TEmployee[]) => void;
    }) => {
      let cancelRequest = false;

      if (!noLoadingIndicator) {
        setIsLoading(true);
      }

      const fetchData = async () => {
        try {
          const result = await getEmployeesRecords({
            ActiveBranchId: activeBranchId,
            date,
            logs: { isLogged: true, userId, database },
          }).unwrap();

          if (!cancelRequest && result) {
            const adaptedRecords = adaptModelEmployeeRecords(result);

            setEmployeeDetails(adaptedRecords);
            setLastStoredEmployeeDetails(adaptedRecords);

            onSuccess?.(adaptedRecords);
          }
        } catch (error) {
          if (!cancelRequest) {
            showNotification(error, WARNING);
          }
        } finally {
          if (!cancelRequest) {
            if (!noLoadingIndicator) {
              setIsLoading(false);
            }
          }
        }
      };

      fetchData();

      return () => {
        cancelRequest = true;
      };
    },
    [
      activeBranchId,
      database,
      showNotification,
      setEmployeeDetails,
      setIsLoading,
      setLastStoredEmployeeDetails,
      userId,
      getEmployeesRecords,
    ]
  );

  const getBranchWorkHoursForDate = useCallback(
    async ({ date }: { date: string }) => {
      if (activeBranchId && date) {
        const response = await getBranchWorkTimesOnDate({
          ActiveBranchId: activeBranchId,
          branchId: activeBranchId,
          dateLocal: date,
        }).unwrap();

        if (response) {
          const { Timezone, WorkTimeS, WorkTimeE, JournalFormatTime } =
            response;
          setTimeFormat(JournalFormatTime);
          setBranchWorkingHoursForDate({ WorkTimeS, WorkTimeE });
          setBranchTimezone(Timezone);
        }
      }
    },
    [
      activeBranchId,
      getBranchWorkTimesOnDate,
      setTimeFormat,
      setBranchWorkingHoursForDate,
      setBranchTimezone,
    ]
  );

  const getBranchEmployeesData = useCallback(
    async ({
      userId,
      database,
      activeBranchId,
      onSuccess,
    }: {
      userId: number;
      database: string;
      activeBranchId: number;
      onSuccess?: (branchEmployees: NEmployees.ModelEmployee[]) => void;
    }) => {
      setIsGetBranchEmployeesLoading(true);
      try {
        const branchEmployees = await getBranchEmployees({
          branchId: activeBranchId,
          ActiveBranchId: activeBranchId,
          logs: { isLogged: true, userId, database },
        }).unwrap();

        if (branchEmployees) {
          onSuccess?.(branchEmployees);
        }
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setIsGetBranchEmployeesLoading(false);
      }
    },
    [getBranchEmployees, setIsGetBranchEmployeesLoading, showNotification]
  );

  return {
    getEmployeesData,
    getBranchWorkHoursForDate,
    getBranchEmployeesData,
    isGetBranchEmployeesLoading,
  };
};
